import { useState } from "react";
import { Eye, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import LoadMore from "../../components/LoadMore";

export default function AdminEmailLog({ ctx }) {
  const { emailLogs } = ctx;
  const [preview, setPreview] = useState(null); // { meta, html }
  const [loadingId, setLoadingId] = useState(null);

  const viewEmail = async (e) => {
    setLoadingId(e.id);
    try {
      const { data } = await api.get(`/admin/email-logs/${e.id}`);
      if (!data.html) {
        toast.info("No preview saved for this email (it was sent before previews were enabled).");
      } else {
        setPreview({ meta: e, html: data.html });
      }
    } catch (err) {
      toast.error("Couldn't load the email preview.");
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="mt-10 space-y-2" data-testid="admin-emaillog">
      <p className="text-sm text-stone-500">A record of every email the site has sent — confirmations, reminders, gift codes, party &amp; creator replies, and staff alerts. Click <b>View</b> to see the exact email that went out. Newest first (last 500).</p>
      {emailLogs.length === 0 ? <p className="text-stone-500">No emails sent yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-stone-200 rounded-xl overflow-hidden">
            <thead className="bg-stone-100 text-left text-xs uppercase tracking-[0.15em] text-stone-500">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {emailLogs.map((e) => (
                <tr key={e.id} data-testid={`email-log-${e.id}`} className="border-t border-stone-100">
                  <td className="px-4 py-3 whitespace-nowrap text-stone-500">{new Date(e.created_at).toLocaleString()}</td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 bg-[#F2E9D8] text-[#121212] text-xs font-bold rounded">{e.type}</span></td>
                  <td className="px-4 py-3 text-stone-700">{e.to}</td>
                  <td className="px-4 py-3 text-stone-700">{e.subject}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${e.status === "sent" ? "bg-emerald-100 text-emerald-800" : e.status === "failed" ? "bg-red-100 text-red-700" : "bg-stone-200 text-stone-600"}`}>{e.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`view-email-${e.id}`}
                      onClick={() => viewEmail(e)}
                      disabled={loadingId === e.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-stone-300 text-stone-600 hover:border-[#121212] hover:text-[#121212] text-xs tracking-[0.1em] uppercase font-bold rounded-lg transition-colors disabled:opacity-50"
                    >
                      {loadingId === e.id ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMore ctx={ctx} pkey="emaillog" label="Load older emails" />

      {preview && (
        <div
          data-testid="email-preview-modal"
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setPreview(null)}
        >
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-stone-200">
              <div className="min-w-0">
                <p className="font-serif font-black text-lg text-[#121212] truncate">{preview.meta.subject}</p>
                <p className="text-xs text-stone-500 truncate">To {preview.meta.to} · {new Date(preview.meta.created_at).toLocaleString()}</p>
              </div>
              <button data-testid="email-preview-close" onClick={() => setPreview(null)} className="p-1.5 text-stone-500 hover:text-[#121212] shrink-0"><X size={20} /></button>
            </div>
            <iframe
              data-testid="email-preview-frame"
              title="Email preview"
              srcDoc={preview.html}
              sandbox=""
              className="flex-1 w-full bg-[#efe9dd]"
              style={{ minHeight: "60vh", border: "none" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
